import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { MessageCircle, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { adminApi } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';
import {
  type ConversationStatus,
  isUnread,
  listChannelMessages,
  listChannels,
  listConversations,
  listMessages,
  postChannelMessage,
  updateConversation,
} from '@/lib/messages';
import { supabase } from '@/lib/supabase';

/**
 * Messages.
 *
 * Two inboxes, because a shop has two: what shoppers ask through the
 * storefront's chat widget, and what the team says to each other. They share
 * a screen rather than a mechanism -- a support conversation belongs to the
 * account and is answered by whoever is free, while a channel post belongs
 * to the person who wrote it.
 *
 * Both are subscribed rather than polled: an inbox that needs refreshing is
 * an inbox nobody watches.
 */
export const Route = createFileRoute('/messages/')({
  component: MessagesPage,
});

const STATUS_TABS: { value: ConversationStatus | 'all'; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

function MessagesPage() {
  const { activeAccountId } = useAuth();
  const accountId = activeAccountId ?? '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Messages</h1>
        <p className="text-muted-foreground text-sm">
          Shopper conversations from the storefront, and the team's own channels.
        </p>
      </div>

      <Tabs defaultValue="support">
        <TabsList>
          <TabsTrigger value="support">Shoppers</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="support" className="mt-4">
          <SupportInbox accountId={accountId} />
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <TeamChat accountId={accountId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SupportInbox({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  const [status, setStatus] = useState<ConversationStatus | 'all'>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const conversations = useQuery({
    queryKey: ['support-conversations', accountId, status],
    queryFn: () => listConversations(accountId, status),
    enabled: Boolean(accountId),
  });

  const selected =
    conversations.data?.find((conversation) => conversation.id === selectedId) ?? null;

  const messages = useQuery({
    queryKey: ['support-messages', accountId, selectedId],
    queryFn: () => listMessages(accountId, selectedId as string),
    enabled: Boolean(accountId && selectedId),
  });

  useEffect(() => {
    if (!accountId) return;

    const channel = supabase
      .channel(`support:${accountId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_messages',
          filter: `account_id=eq.${accountId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['support-messages', accountId] });
          queryClient.invalidateQueries({ queryKey: ['support-conversations', accountId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [accountId, queryClient]);

  // Opening a conversation is reading it.
  useEffect(() => {
    if (!selectedId) return;

    updateConversation(accountId, selectedId, { markRead: true })
      .then(() => queryClient.invalidateQueries({ queryKey: ['support-conversations', accountId] }))
      .catch(() => undefined);
  }, [accountId, selectedId, queryClient]);

  const reply = useMutation({
    mutationFn: () => adminApi.support.reply(accountId, selectedId as string, draft.trim()),
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['support-messages', accountId, selectedId] });
      queryClient.invalidateQueries({ queryKey: ['support-conversations', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const setState = useMutation({
    mutationFn: (patch: { status?: ConversationStatus; assignedTo?: string | null }) =>
      updateConversation(accountId, selectedId as string, patch),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['support-conversations', accountId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1">
          {STATUS_TABS.map((tab) => (
            <Button
              key={tab.value}
              type="button"
              size="sm"
              variant={status === tab.value ? 'secondary' : 'ghost'}
              onClick={() => setStatus(tab.value)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {conversations.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : conversations.data?.length ? (
          <ul className="divide-y rounded-lg border">
            {conversations.data.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(conversation.id)}
                  className={`hover:bg-muted/50 w-full px-3 py-2.5 text-left ${
                    conversation.id === selectedId ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium">{conversation.displayName}</span>
                    {isUnread(conversation) && <Badge className="shrink-0">New</Badge>}
                  </div>
                  <p className="text-muted-foreground truncate text-xs">
                    {conversation.lastMessagePreview ?? 'No messages yet'}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>
            <MessageCircle className="mx-auto mb-2 size-6" />
            Nothing here.
          </EmptyState>
        )}
      </div>

      {selected ? (
        <div className="flex min-h-[28rem] flex-col rounded-lg border">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
            <div className="min-w-0">
              <p className="truncate font-medium">{selected.displayName}</p>
              <p className="text-muted-foreground truncate text-xs">
                {selected.email ?? 'No email given'}
                {selected.startedPath ? ` · from ${selected.startedPath}` : ''}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setState.mutate({ assignedTo: session?.user.id ?? null })}
              >
                {selected.assignedTo === session?.user.id ? 'Assigned to you' : 'Assign to me'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setState.mutate({ status: 'snoozed' })}
              >
                Snooze
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setState.mutate({ status: 'closed' })}
              >
                Close
              </Button>
            </div>
          </header>

          <MessageList
            items={(messages.data ?? []).map((message) => ({
              id: message.id,
              mine: message.sender === 'staff',
              author: message.authorName ?? (message.sender === 'visitor' ? 'Visitor' : 'System'),
              body: message.body ?? '',
              createdAt: message.createdAt,
            }))}
            loading={messages.isLoading}
          />

          <form
            className="flex items-end gap-2 border-t p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft.trim()) reply.mutate();
            }}
            noValidate
          >
            <Textarea
              rows={2}
              value={draft}
              placeholder="Write a reply…"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends; Shift+Enter is a new line. A support reply is
                // a sentence, not a document.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (draft.trim()) reply.mutate();
                }
              }}
            />
            <Button type="submit" disabled={!draft.trim() || reply.isPending}>
              <Send className="size-4" />
              Send
            </Button>
          </form>
        </div>
      ) : (
        <EmptyState>Pick a conversation.</EmptyState>
      )}
    </div>
  );
}

function TeamChat({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  const [channelId, setChannelId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const channels = useQuery({
    queryKey: ['chat-channels', accountId],
    queryFn: () => listChannels(accountId),
    enabled: Boolean(accountId),
  });

  const activeId = channelId ?? channels.data?.[0]?.id ?? null;

  const messages = useQuery({
    queryKey: ['chat-messages', accountId, activeId],
    queryFn: () => listChannelMessages(accountId, activeId as string),
    enabled: Boolean(accountId && activeId),
  });

  useEffect(() => {
    if (!accountId) return;

    const channel = supabase
      .channel(`chat:${accountId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `account_id=eq.${accountId}`,
        },
        () => queryClient.invalidateQueries({ queryKey: ['chat-messages', accountId] }),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [accountId, queryClient]);

  const post = useMutation({
    mutationFn: () =>
      postChannelMessage(accountId, {
        channelId: activeId as string,
        userId: session?.user.id as string,
        body: draft.trim(),
      }),
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['chat-messages', accountId, activeId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
      <ul className="divide-y rounded-lg border">
        {(channels.data ?? []).map((channel) => (
          <li key={channel.id}>
            <button
              type="button"
              onClick={() => setChannelId(channel.id)}
              className={`hover:bg-muted/50 w-full px-3 py-2 text-left text-sm ${
                channel.id === activeId ? 'bg-muted font-medium' : ''
              }`}
            >
              #{channel.name}
            </button>
          </li>
        ))}
        {!channels.data?.length && (
          <li className="text-muted-foreground p-3 text-xs">No channels yet.</li>
        )}
      </ul>

      <div className="flex min-h-[28rem] flex-col rounded-lg border">
        <MessageList
          items={(messages.data ?? []).map((message) => ({
            id: message.id,
            mine: message.userId === session?.user.id,
            author: message.userId === session?.user.id ? 'You' : 'Teammate',
            body: message.body,
            createdAt: message.createdAt,
          }))}
          loading={messages.isLoading}
        />

        <form
          className="flex items-end gap-2 border-t p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim() && activeId) post.mutate();
          }}
          noValidate
        >
          <Textarea
            rows={2}
            value={draft}
            placeholder="Message the team…"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (draft.trim() && activeId) post.mutate();
              }
            }}
          />
          <Button type="submit" disabled={!draft.trim() || !activeId || post.isPending}>
            <Send className="size-4" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
}

/** Both inboxes render the same thing: who said what, oldest first. */
function MessageList({
  items,
  loading,
}: {
  items: { id: string; mine: boolean; author: string; body: string; createdAt: string }[];
  loading: boolean;
}) {
  const bottom = useRef<HTMLDivElement>(null);

  // Scrolls as the list grows.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [items.length]);

  if (loading) return <Skeleton className="m-4 h-64" />;

  if (!items.length) {
    return <p className="text-muted-foreground flex-1 p-6 text-center text-sm">No messages yet.</p>;
  }

  return (
    <div className="flex-1 space-y-3 overflow-y-auto p-4">
      {items.map((item) => (
        <div key={item.id} className={`flex ${item.mine ? 'justify-end' : 'justify-start'}`}>
          <div
            className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
              item.mine ? 'bg-primary text-primary-foreground' : 'bg-muted'
            }`}
          >
            <p className="mb-0.5 text-[11px] opacity-70">
              {item.author} ·{' '}
              {new Intl.DateTimeFormat('en-US', { timeStyle: 'short' }).format(
                new Date(item.createdAt),
              )}
            </p>
            <p className="whitespace-pre-wrap">{item.body}</p>
          </div>
        </div>
      ))}
      <div ref={bottom} />
    </div>
  );
}
